import React, { useEffect, useState } from 'react';
import {
  Alert, Button, Card, CardHeader, EmptyState, Metric, Table, Tabs, Toggle,
} from '@opendate-app/setlist';
import { ChevronDown, ChevronRight, Download01 } from '@untitled-ui/icons-react';
import AttributionChart from './AttributionChart';
import WindowPicker from './WindowPicker';
import {
  DATE_MODE_TABS, GROUP_TABS, METRIC_TABS, MODEL_TABS, PRODUCT_LABELS, WINDOW_PRESETS,
  buildReportParams, chartCaption, formatCents, formatCount, formatCoverage, formatRoas, formatTrackingDate, rangeErrorFor, tableRows, toQueryString,
} from './attributionReportUtils';

const axios = require('axios').default;

const noteStyle = { fontSize: 12, color: 'var(--sl-text-tertiary)', margin: 0 };
const refreshingStyle = { opacity: 0.5, transition: 'opacity 150ms' };

function ChannelCell({ row, expanded, onToggle }) {
  if (row.depth > 0) {
    return <span style={{ paddingLeft: 32, color: 'var(--sl-text-secondary)' }}>{row.label}</span>;
  }
  if (!row.expandable) {
    return <span style={{ paddingLeft: 28, fontWeight: 600 }}>{row.label}</span>;
  }
  return (
    <Button
      variant="tertiary"
      size="sm"
      icon={expanded ? ChevronDown : ChevronRight}
      aria-expanded={expanded}
      aria-label={`${expanded ? 'Collapse' : 'Expand'} ${row.label}`}
      onClick={() => onToggle(row.key)}
    >
      {row.label}
    </Button>
  );
}

const groupHeader = (groupBy) => GROUP_TABS.find((tab) => tab.value === groupBy)?.label || 'Channel';

const tableColumns = (groupBy, expandedKeys, onToggle) => [
  {
    key: 'label',
    header: groupHeader(groupBy),
    primary: true,
    render: (row) => <ChannelCell row={row} expanded={!!expandedKeys[row.key]} onToggle={onToggle} />,
  },
  { key: 'revenue_cents', header: 'Revenue', align: 'right', render: (row) => formatCents(row.revenue_cents) },
  { key: 'tickets', header: 'Tickets', align: 'right', render: (row) => formatCount(row.tickets) },
  { key: 'orders', header: 'Orders', align: 'right', render: (row) => formatCount(row.orders) },
  {
    key: 'influenced_orders',
    header: 'Influenced',
    align: 'right',
    render: (row) => `${formatCount(row.influenced_orders)} · ${formatCents(row.influenced_revenue_cents)}`,
  },
  { key: 'roas', header: 'ROAS', align: 'right', render: (row) => formatRoas(row.roas) },
];

const productColumns = [
  { key: 'label', header: 'Product', primary: true, render: (row) => row.label },
  { key: 'revenue_cents', header: 'Revenue', align: 'right', render: (row) => formatCents(row.revenue_cents) },
  { key: 'tickets', header: 'Tickets', align: 'right', render: (row) => formatCount(row.tickets) },
  { key: 'orders', header: 'Orders', align: 'right', render: (row) => formatCount(row.orders) },
];

function AttributionReport({
  teamId, basePath, dateRange, filters, calendarEventIds, defaultWindowDays = WINDOW_PRESETS[3], trackingStartedAt, maxWindowDays = 120, showDateMode = true,
}) {
  const [model, setModel] = useState('last');
  const [windowDays, setWindowDays] = useState(defaultWindowDays);
  const [dateMode, setDateMode] = useState('order_date');
  const [includeRenewals, setIncludeRenewals] = useState(false);
  const [metric, setMetric] = useState('revenue');
  const [groupBy, setGroupBy] = useState('channel');
  const [expandedKeys, setExpandedKeys] = useState({});
  const [report, setReport] = useState({ loading: true, data: null, error: null });

  const base = basePath || `/teams/${teamId}/reporting/marketing/attribution`;
  const appliedFilters = report.data?.filters;
  const appliedRangeDiffers = !report.loading && Boolean(appliedFilters?.start_date && appliedFilters?.end_date)
    && (appliedFilters.start_date !== dateRange?.start || appliedFilters.end_date !== dateRange?.end);
  const rangeError = rangeErrorFor(dateRange);
  const params = buildReportParams({
    dateRange, model, windowDays, dateMode, includeRenewals, filters, calendarEventIds,
  });
  const paramsKey = toQueryString(params);
  const exportKey = toQueryString({ ...params, group_by: groupBy });

  useEffect(() => {
    if (rangeError) {
      setReport({ loading: false, data: null, error: null });
      return undefined;
    }
    const controller = new AbortController();
    setReport((previous) => ({ ...previous, loading: true, error: null }));
    axios.get(`${base}/data.json?${paramsKey}`, { signal: controller.signal })
      .then(({ data }) => {
        if (!controller.signal.aborted) setReport({ loading: false, data, error: null });
      })
      .catch((error) => {
        if (controller.signal.aborted) return;
        console.error('Failed to load attribution report', error);
        setReport({ loading: false, data: null, error: error?.response?.data?.error || 'Could not load the attribution report.' });
      });
    return () => controller.abort();
  }, [base, paramsKey, rangeError]);

  const toggleRow = (key) => setExpandedKeys((previous) => ({ ...previous, [key]: !previous[key] }));
  const rows = report.data?.groupings?.[groupBy] || [];
  const totals = report.data?.totals;
  const productTotals = Object.entries(report.data?.product_totals || {})
    .map(([key, value]) => ({ key, label: PRODUCT_LABELS[key] || key, ...value }));
  const message = rangeError || report.error;
  const eventFiltered = params.calendar_event_id != null;
  const refreshing = report.loading && report.data != null;

  return (
    <div>
      <div className="d-flex align-items-center flex-wrap mb-3" style={{ gap: 12 }}>
        <Tabs variant="button-border" size="sm" collapseBelow={0} aria-label="Attribution model" items={MODEL_TABS} value={model} onChange={setModel} />
        <WindowPicker windowDays={windowDays} defaultWindowDays={defaultWindowDays} maxWindowDays={maxWindowDays} onChange={setWindowDays} />
        {showDateMode && <Tabs variant="button-border" size="sm" collapseBelow={0} aria-label="Date mode" items={DATE_MODE_TABS} value={dateMode} onChange={setDateMode} />}
        <Toggle size="sm" label="Include renewals" checked={includeRenewals} onChange={(value) => setIncludeRenewals(value?.target ? value.target.checked : Boolean(value))} />
        <div className="ml-auto">
          <Button as="a" variant="secondary" size="sm" icon={Download01} href={rangeError ? undefined : `${base}/export.csv?${exportKey}`} disabled={!!rangeError}>
            Export CSV
          </Button>
        </div>
      </div>

      <div className="d-flex flex-column mb-3" style={{ gap: 4 }}>
        <p style={noteStyle}>
          {trackingStartedAt
            ? `Tracking since ${formatTrackingDate(trackingStartedAt)}. Sales before then show as Unknown.`
            : 'Tracking hasn\'t started for this team yet, so every sale shows as Unknown.'}
        </p>
        {appliedRangeDiffers && (
          <p style={noteStyle} data-note="applied-range">
            {`Showing ${formatTrackingDate(appliedFilters.start_date)} to ${formatTrackingDate(appliedFilters.end_date)}, the dates this report covers.`}
          </p>
        )}
        {eventFiltered && (
          <p style={noteStyle} data-note="event-filter">
            Gift cards and classes aren&apos;t sold for one event, so they&apos;re left out when you filter by event.
          </p>
        )}
        {dateMode === 'event_date' && (
          <p style={noteStyle} data-note="event-date">
            By event date, sales count on the show&apos;s date. Gift cards, memberships and membership renewals still count on their purchase or payment date, while packs and campaign donations with no event are left out, matching the Sales report.
          </p>
        )}
      </div>

      {message && <div className="mb-3"><Alert color={rangeError ? 'warning' : 'error'} title={message} /></div>}

      <div aria-busy={refreshing} style={refreshing ? refreshingStyle : undefined}>
      <div style={{ display: 'grid', gridTemplateColumns: 'repeat(auto-fit, minmax(180px, 1fr))', gap: 16, marginBottom: 16 }}>
        {[
          ['Revenue', totals && formatCents(totals.revenue_cents)],
          ['Tickets', totals && formatCount(totals.tickets)],
          ['Orders', totals && formatCount(totals.orders)],
          ['Coverage', report.data && formatCoverage(report.data.coverage_pct)],
        ].map(([label, value]) => <Metric key={label} label={label} value={value || '—'} />)}
      </div>

      <div className="mb-3">
        <Card
          header={(
            <CardHeader
              title={`Sales by ${groupHeader(groupBy).toLowerCase()}`}
              supportingText={chartCaption(metric)}
              actions={<Tabs variant="button-border" size="sm" collapseBelow={0} aria-label="Chart metric" items={METRIC_TABS} value={metric} onChange={setMetric} />}
            />
          )}
        >
          {rows.length > 0
            ? <AttributionChart rows={rows} metric={metric} />
            : <EmptyState size="sm" title={report.loading ? 'Loading…' : 'No sales in this range'} />}
        </Card>
      </div>
      </div>

      <div className="mb-3">
        <Card
          padding="none"
          header={(
            <CardHeader
              title="Breakdown"
              supportingText={groupBy === 'channel' ? 'Expand a channel to see its sources.' : `One row per ${groupHeader(groupBy).toLowerCase()}.`}
              actions={<Tabs variant="button-border" size="sm" collapseBelow={0} aria-label="Group by" items={GROUP_TABS} value={groupBy} onChange={setGroupBy} />}
            />
          )}
        >
          <Table
            columns={tableColumns(groupBy, expandedKeys, toggleRow)}
            data={tableRows(rows, expandedKeys)}
            getRowKey={(row) => row.rowKey}
            loading={report.loading}
            empty={<EmptyState size="sm" title="No sales in this range" />}
          />
        </Card>
      </div>

      {productTotals.length > 0 && (
        <div className="mb-3">
          <Card padding="none" header={<CardHeader title="Not included in the totals" supportingText="Classes and resale have no Sales report section, so they are shown on their own." />}>
            <Table columns={productColumns} data={productTotals} getRowKey={(row) => row.key} />
          </Card>
          {productTotals.some((row) => row.key === 'resale') && (
            <p style={{ ...noteStyle, marginTop: 8 }}>
              Resale orders include resales fully refunded in the same period, which count as one order with $0 and 0 tickets.
            </p>
          )}
        </div>
      )}

      <div className="d-flex flex-column" style={{ gap: 4 }}>
        <p style={noteStyle}>Comp / Guest List orders show tickets at $0 and are left out of coverage. Renewals sit in their own row unless you include them.</p>
        <p style={noteStyle}>ROAS uses Meta spend and shows only on Meta rows. Spend from ad accounts in a currency other than the team&apos;s is left out.</p>
      </div>
    </div>
  );
}

export default AttributionReport;
